import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { Countdown as StandardCountdown } from '@sorare/core/src/components/Countdown';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { useTickerContext } from '@sorare/core/src/contexts/ticker';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { LeagueLaunch as LeagueLaunchType } from '@sorare/marketplace/src/lib/newSeason';
import {
  getLeagueAvatar,
  getLeagueBackgroundUrl,
  useSecondaryLeagueLabel,
} from '@sorare/marketplace/src/lib/newSeason';

import { ExpandedLeagueLaunch } from '#src/components/new-season/ExpandedLeagueLaunch/index.tsx';

import { Countdown } from './Countdown';
import type {
  LeagueLaunch_competition,
  LeagueLaunch_so5Competition,
} from './__generated__/index.graphql';
import upcoming from './assets/upcoming.webp';

const Root = styled(SBHorizontal)`
  --launch-bg: url(${upcoming});
  border-radius: var(--double-unit);
  background:
    linear-gradient(
      90deg,
      rgba(var(--c-rgb-black), 0.8),
      rgba(var(--c-rgb-black), 0)
    ),
    var(--launch-bg) no-repeat center center/cover;
  padding: var(--unit) var(--double-unit);

  @container ${tabletAndAbove} {
    border-radius: var(--triple-unit);
    padding: var(--double-unit) var(--quadruple-unit);
  }
`;

const LeagueInfo = styled(Horizontal)`
  flex-shrink: 1;
  word-break: break-word;
`;

const LeagueLogoContainer = styled.div`
  flex-shrink: 0;
  width: calc(var(--unit) * 6);
  height: calc(var(--unit) * 6);
  @container ${tabletAndAbove} {
    width: calc(var(--unit) * 8);
    height: calc(var(--unit) * 8);
  }
`;

const StyledLeagueAvatar = styled(LeagueAvatar)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const LeagueName = styled(BrandS)`
  @container ${tabletAndAbove} {
    font: var(--t-brand-m);
  }
`;

type Props = {
  leagueLaunch: LeagueLaunchType<
    LeagueLaunch_competition,
    LeagueLaunch_so5Competition
  >;
};

export const LeagueLaunch = ({ leagueLaunch }: Props) => {
  const { now } = useTickerContext();
  const isLaptop = useIsLaptop();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { competition, startDate, slug } = leagueLaunch;
  const label = useSecondaryLeagueLabel(leagueLaunch);
  const backgroundPictureUrl = getLeagueBackgroundUrl(leagueLaunch);
  const leagueAvatar = getLeagueAvatar(leagueLaunch);
  const leagueNode =
    (!!leagueAvatar?.avatarUrl && (
      <LeagueLogoContainer>
        <StyledLeagueAvatar league={leagueAvatar} />
      </LeagueLogoContainer>
    )) ||
    leagueLaunch?.iconNode;
  const isLive = startDate < now;
  if (isLive) {
    return <ExpandedLeagueLaunch isLive leagueLaunch={leagueLaunch} />;
  }
  return (
    <Root
      id={slug}
      style={
        {
          ...(backgroundPictureUrl && {
            '--launch-bg': `url('${backgroundPictureUrl}')`,
          }),
        } as CSSProperties
      }
    >
      <LeagueInfo gap={isTabletAndAbove ? 3 : 2}>
        {leagueNode}
        <Vertical>
          {!!label && <BrandXXS color="var(--c-nd-600)">{label}</BrandXXS>}
          <LeagueName>{competition?.displayName}</LeagueName>
        </Vertical>
      </LeagueInfo>
      {isLaptop ? (
        <StandardCountdown date={startDate} greyOutZeros Label={BrandM} />
      ) : (
        <Countdown date={startDate} />
      )}
    </Root>
  );
};

LeagueLaunch.fragments = {
  competition: gql`
    fragment LeagueLaunch_competition on Competition {
      slug
      name
      displayName
      ...ExpandedLeagueLaunch_competition
      ...getLeagueAvatar_competition
      ...getLeagueBackgroundUrl_competition
    }
    ${ExpandedLeagueLaunch.fragments.competition}
    ${getLeagueAvatar.fragments.competition}
    ${getLeagueBackgroundUrl.fragments.competition}
  ` as TypedDocumentNode<LeagueLaunch_competition>,
  so5Competition: gql`
    fragment LeagueLaunch_so5Competition on So5Competition {
      slug
      displayName
      competitions {
        slug
      }
      ...ExpandedLeagueLaunch_so5Competition
      ...getLeagueAvatar_so5Competition
      ...getLeagueBackgroundUrl_so5Competition
    }
    ${ExpandedLeagueLaunch.fragments.so5Competition}
    ${getLeagueAvatar.fragments.so5Competition}
    ${getLeagueBackgroundUrl.fragments.so5Competition}
  ` as TypedDocumentNode<LeagueLaunch_so5Competition>,
};
